import { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import Chart, { ChartConfiguration } from 'chart.js/auto';
import 'chartjs-adapter-moment';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  lineChart: {
    backgroundColor: 'white',
    borderRadius: 16,
    minHeight: 300,
    height: '100%',
    padding: '20px 20px 10px',
  },
  lineChart__chart: {
    width: '100%',
    paddingBottom: 10,
  },
  lineChart__text: {
    color: theme.palette.gray.g1,
    fontSize: 14,
    fontWeight: 900,
  },
}));

const LineChart = ({
  data,
  title,
  labels,
  yAxisTickFormatFn = (v) => v,
}: {
  data: number[];
  title: string;
  labels: string[];
  yAxisTickFormatFn?: (value: number | string) => number | string;
}) => {
  const classes = useStyles();
  const [chart, setChart] = useState<Chart | null>(null);
  const lineChart = useRef<HTMLCanvasElement | null>(null);

  const config: ChartConfiguration = useMemo(
    () => ({
      type: 'line',
      data: {
        labels: [],
        datasets: [
          {
            data: [],
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.2,
          },
        ],
      },
      options: {
        animation: false,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: function (items) {
                if (!items.length) return '';
                return items[0].label?.split(',').slice(0, 2).join(',').trim() || '';
              },
            },
          },
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 4,
            hitRadius: 20,
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: 'MMM YYYY',
              },
              tooltipFormat: 'YYYY-MM-DD',
            },
            grid: {
              borderColor: 'transparent',
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10,
              maxRotation: 20,
              minRotation: 0,
              color: 'black',
              font: {
                weight: '900',
              },
              // The date adapter formats in moment's global locale, which the vacation planner's
              // Swedish locale import changes. Keep the axis in English like the rest of the app.
              callback: function (_label, index, ticks) {
                const { unit, displayFormats } = (this as any).options.time;
                return moment(ticks[index].value).locale('en').format(displayFormats[unit]);
              },
            },
          },
          y: {
            grid: {
              borderColor: 'transparent',
              display: false,
            },
            ticks: {
              padding: 10,
              maxTicksLimit: 4,
              color: 'black',
              stepSize: 0.05,
              precision: 2,
              font: {
                weight: '900',
              },
              callback: function (value, _index, _values) {
                return yAxisTickFormatFn(value);
              },
            },
          },
        },
      },
    }),
    [],
  );

  useEffect(() => {
    if (lineChart && lineChart.current && !chart) {
      let newChart = new Chart(lineChart.current, config);
      setChart(newChart);
    }
  }, [lineChart, chart, config]);

  useEffect(() => {
    if (chart && data.length > 0) {
      chart.data.labels = labels;
      chart.data.datasets[0].data = data;
      // Pick time unit based on date range
      const xScale = chart.options.scales?.x as any;
      if (xScale?.time && labels.length >= 2) {
        const rangeMs = new Date(labels[labels.length - 1]).getTime() - new Date(labels[0]).getTime();
        const rangeDays = rangeMs / (1000 * 60 * 60 * 24);
        xScale.time.unit = rangeDays < 60 ? 'day' : 'month';
        xScale.time.displayFormats = rangeDays < 60
          ? { day: 'MMM D' }
          : { month: 'MMM YYYY' };
      }
      chart.update();
    }
  });

  return (
    <div className={classes.lineChart}>
      <div className={classes.lineChart__chart}>
        <canvas ref={lineChart} height={260} />
      </div>
      <div className={classes.lineChart__text}>{title}</div>
    </div>
  );
};
export default LineChart;
